const GOOGLE_MAPS_SCRIPT_ID = 'n3bula-google-maps-js';
let googleMapsPromise = null;

export const DRAWING_BASE_MAP_ZOOM = 21;
export const MAX_RELIABLE_STATIC_MAP_ZOOM = DRAWING_BASE_MAP_ZOOM;

export function clampReliableStaticMapZoom(value, fallback = 20) {
  const parsed = Number(value);
  const base = Number.isFinite(parsed) ? parsed : fallback;
  return Math.max(0, Math.min(MAX_RELIABLE_STATIC_MAP_ZOOM, Math.round(base)));
}


export function getGoogleMapsApiKey() {
  try {
    return (
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
      process.env.GOOGLE_MAPS_API_KEY ||
      process.env.VITE_GOOGLE_MAPS_API_KEY ||
      ''
    );
  } catch {
    return '';
  }
}

export function hasGoogleMapsApiKey() {
  return Boolean(getGoogleMapsApiKey());
}

export function loadGoogleMaps() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in the browser.'));
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return Promise.reject(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY in the root .env file.'));
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      existingScript.addEventListener('error', () => reject(new Error('Google Maps failed to load.')));
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    script.onload = () => {
      if (window.google?.maps?.places) resolve(window.google);
      else reject(new Error('Google Maps loaded, but Places library is unavailable.'));
    };
    script.onerror = () => reject(new Error('Google Maps failed to load. Check your API key, billing, and enabled APIs.'));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

export function placeToAddressData(place) {
  if (!place) return null;
  const location = place.geometry?.location;
  return {
    formattedAddress: place.formatted_address || place.name || '',
    name: place.name || '',
    location: location
      ? {
          lat: typeof location.lat === 'function' ? location.lat() : location.lat,
          lng: typeof location.lng === 'function' ? location.lng() : location.lng,
        }
      : null,
  };
}

export function googleStaticMapPixelsPerInch(lat, zoom = 21, scale = 1) {
  const safeLat = Number.isFinite(Number(lat)) ? Number(lat) : 0;
  const cos = Math.cos((safeLat * Math.PI) / 180);
  const metersPerPixel = (156543.03392 * Math.max(cos, 0.0001)) / Math.pow(2, zoom) / Math.max(scale, 0.0001);
  const feetPerPixel = metersPerPixel * 3.28084;
  return 1 / (feetPerPixel * 12);
}

export function getGoogleStaticMapDimensions({ width = 640, height = 640, scale = 2, preserveAspect = false } = {}) {
  const safeScale = Math.max(1, Math.min(2, Number(scale) || 2));
  const rawWidth = Math.max(Math.round(Number(width) || 640), 1);
  const rawHeight = Math.max(Math.round(Number(height) || 640), 1);
  let cssWidth = rawWidth;
  let cssHeight = rawHeight;

  if (preserveAspect) {
    const fit = Math.min(640 / rawWidth, 640 / rawHeight, 1);
    cssWidth = Math.max(1, Math.round(rawWidth * fit));
    cssHeight = Math.max(1, Math.round(rawHeight * fit));
  } else {
    cssWidth = Math.min(Math.max(rawWidth, 1), 640);
    cssHeight = Math.min(Math.max(rawHeight, 1), 640);
  }

  return {
    cssWidth,
    cssHeight,
    pixelWidth: cssWidth * safeScale,
    pixelHeight: cssHeight * safeScale,
    scale: safeScale,
  };
}

export function latLngToWorldPixel({ lat, lng }, zoom) {
  const siny = Math.sin((Number(lat) * Math.PI) / 180);
  const safeSiny = Math.min(Math.max(siny, -0.9999), 0.9999);
  const worldSize = 256 * Math.pow(2, zoom);
  return {
    x: worldSize * (0.5 + Number(lng) / 360),
    y: worldSize * (0.5 - Math.log((1 + safeSiny) / (1 - safeSiny)) / (4 * Math.PI)),
  };
}

export function worldPixelToLatLng({ x, y }, zoom) {
  const worldSize = 256 * Math.pow(2, zoom);
  const lng = (x / worldSize - 0.5) * 360;
  const n = Math.PI - (2 * Math.PI * y) / worldSize;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return { lat, lng };
}

export function offsetLatLngByPixels(center, dx, dy, zoom) {
  const centerPixel = latLngToWorldPixel(center, zoom);
  return worldPixelToLatLng({ x: centerPixel.x + dx, y: centerPixel.y + dy }, zoom);
}


export function mapPixelToLatLng(point, center, zoom, width, height) {
  if (!point || !center?.lat || !center?.lng) return null;
  const centerPixel = latLngToWorldPixel(center, zoom);
  return worldPixelToLatLng({
    x: centerPixel.x + Number(point.x || 0) - Number(width || 0) / 2,
    y: centerPixel.y + Number(point.y || 0) - Number(height || 0) / 2,
  }, zoom);
}

export function latLngToMapPixel(location, center, zoom, width, height) {
  if (!location || !center?.lat || !center?.lng) return null;
  const centerPixel = latLngToWorldPixel(center, zoom);
  const targetPixel = latLngToWorldPixel(location, zoom);
  return {
    x: Number(width || 0) / 2 + (targetPixel.x - centerPixel.x),
    y: Number(height || 0) / 2 + (targetPixel.y - centerPixel.y),
  };
}

export function buildGoogleStaticMapUrl({ lat, lng, zoom = 21, width = 640, height = 640, mapType = 'satellite', scale = 2, preserveAspect = false }) {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey || lat == null || lng == null) return '';
  const dimensions = getGoogleStaticMapDimensions({ width, height, scale, preserveAspect });
  const params = new URLSearchParams({
    center: `${lat},${lng}`,
    zoom: String(zoom),
    size: `${dimensions.cssWidth}x${dimensions.cssHeight}`,
    scale: String(dimensions.scale),
    maptype: mapType,
    key: apiKey,
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

export function createStaticMapTiles({ location, zoom = 20, width = 1280, height = 720, scale = 2, mapType = 'satellite' } = {}) {
  if (!location?.lat || !location?.lng) return [];
  const safeWidth = Math.max(320, Math.round(Number(width) || 1280));
  const safeHeight = Math.max(320, Math.round(Number(height) || 720));
  const tileMax = 640;
  const tiles = [];
  for (let y = 0; y < safeHeight; y += tileMax) {
    const tileHeight = Math.min(tileMax, safeHeight - y);
    for (let x = 0; x < safeWidth; x += tileMax) {
      const tileWidth = Math.min(tileMax, safeWidth - x);
      const dx = x + tileWidth / 2 - safeWidth / 2;
      const dy = y + tileHeight / 2 - safeHeight / 2;
      const tileCenter = offsetLatLngByPixels(location, dx, dy, zoom);
      tiles.push({
        x,
        y,
        width: tileWidth,
        height: tileHeight,
        scale: Math.max(1, Math.min(2, Number(scale) || 2)),
        url: buildGoogleStaticMapUrl({ lat: tileCenter.lat, lng: tileCenter.lng, zoom, width: tileWidth, height: tileHeight, scale, mapType }),
      });
    }
  }
  return tiles;
}

export function createMapConfigForLocation(location, options = {}) {
  if (!location?.lat || !location?.lng) return {};
  const requestedZoom = Number(options.zoom || 20);
  const captureZoom = clampReliableStaticMapZoom(requestedZoom, 20);
  const scale = options.scale || 2;
  const requestedWidth = Math.max(320, Math.round(Number(options.previewWidth || options.width || 640)));
  const requestedHeight = Math.max(320, Math.round(Number(options.previewHeight || options.height || 640)));
  const mapType = 'satellite';

  // Keep the actual static snapshot zoom stable and deterministic. Google Satellite
  // preview can show a 5 m scale at zoom 22, but static satellite tiles are not
  // reliable at that level for every address. Clamping the generated snapshot to
  // the drawing base zoom prevents the image from being rendered at a different
  // real-world scale than the app uses for measurements.
  const capturePixelsPerInch = googleStaticMapPixelsPerInch(location.lat, captureZoom, 1);
  const drawingPixelsPerInch = Number(options.drawingPixelsPerInch)
    || googleStaticMapPixelsPerInch(location.lat, DRAWING_BASE_MAP_ZOOM, 1);
  const displayScale = Math.max(0.05, Math.min(20, drawingPixelsPerInch / Math.max(capturePixelsPerInch, 0.000001)));
  const displayWidth = Math.max(1, Math.round(requestedWidth * displayScale));
  const displayHeight = Math.max(1, Math.round(requestedHeight * displayScale));

  // Fast path: one Google Static Map request can return up to 1280 x 1280
  // actual image pixels when scale=2. The previous logic tiled anything over
  // 640 logical pixels, which made normal 1280 x 720 editor maps load 4+ images.
  // Use one aspect-correct image whenever scale=2 can cover the requested size.
  const maxSingleStaticPixelSize = 640 * Math.max(1, Math.min(2, Number(scale) || 2));
  const shouldTile = requestedWidth > maxSingleStaticPixelSize || requestedHeight > maxSingleStaticPixelSize;
  const rawTiles = shouldTile
    ? createStaticMapTiles({ location, zoom: captureZoom, width: requestedWidth, height: requestedHeight, scale, mapType })
    : [];
  const tiles = rawTiles.map((tile) => ({
    ...tile,
    captureX: tile.x,
    captureY: tile.y,
    captureWidth: tile.width,
    captureHeight: tile.height,
    x: tile.x * displayScale,
    y: tile.y * displayScale,
    width: tile.width * displayScale,
    height: tile.height * displayScale,
  }));

  const dimensions = getGoogleStaticMapDimensions({
    width: requestedWidth,
    height: requestedHeight,
    scale,
    preserveAspect: !shouldTile,
  });
  const imageDataUrl = shouldTile ? '' : buildGoogleStaticMapUrl({
    lat: location.lat,
    lng: location.lng,
    zoom: captureZoom,
    width: requestedWidth,
    height: requestedHeight,
    scale,
    mapType,
    preserveAspect: true,
  });

  return {
    source: 'Google Satellite',
    requestedGoogleStaticZoom: Number.isFinite(requestedZoom) ? Math.round(requestedZoom) : 20,
    googleStaticZoom: captureZoom,
    staticZoomClamped: captureZoom !== (Number.isFinite(requestedZoom) ? Math.round(requestedZoom) : 20),
    googleStaticScale: scale,
    googleStaticCssWidth: shouldTile ? requestedWidth : dimensions.cssWidth,
    googleStaticCssHeight: shouldTile ? requestedHeight : dimensions.cssHeight,
    captureWidth: requestedWidth,
    captureHeight: requestedHeight,
    capturePixelsPerInch,
    drawingBaseZoom: DRAWING_BASE_MAP_ZOOM,
    drawingPixelsPerInch,
    mapDisplayScale: displayScale,
    snapshotPreviewWidth: requestedWidth,
    snapshotPreviewHeight: requestedHeight,
    snapshotUsesPreviewScale: true,
    width: displayWidth,
    height: displayHeight,
    imageDataUrl,
    tiles,
    pixelsPerInch: drawingPixelsPerInch,
    addressLocation: location,
  };
}
